import { apiClient, bearer, unwrap } from './client';
import type { RuleAuthorityListOut, RuleListOut, RuleOut } from './types';

export async function listRules(idToken: string): Promise<RuleListOut> {
  // The schema's RuleListOut has loose `trigger` / `recovery_formula`
  // shapes (Pydantic discriminated unions don't survive JSON-schema);
  // our type override narrows them to the typed `RulePredicate` /
  // `RuleRecoveryFormula`. The wire shape is the same — cast through.
  const data = unwrap<unknown>(
    await apiClient.GET('/api/rules', { headers: bearer(idToken) }),
  );
  return data as RuleListOut;
}

/** Point-lookup for a specific (rule_id, version) — used by the finding
 *  card when a historical-version finding references a rule version
 *  that isn't the latest. `listRules` returns latest-only, so the
 *  ruleIndex on the run page misses; this falls back to the
 *  authoritative on-disk YAML for the version the engine evaluated
 *  against. Throws ApiError(404) if the version doesn't exist. */
export async function getRuleVersion(
  ruleId: string,
  version: number,
  idToken: string,
): Promise<RuleOut> {
  const data = unwrap<unknown>(
    await apiClient.GET('/api/rules/{rule_id}/v{version}', {
      params: { path: { rule_id: ruleId, version } },
      headers: bearer(idToken),
    }),
  );
  // Same cast as listRules — the trigger / recovery_formula shapes need
  // the hand-written narrowed types, not the JSON-schema flatten.
  return data as RuleOut;
}

/** A rule's governing CRA authorities (deterministic, design §7.2). Empty
 *  `items` when the rule has no mapped authority. */
export async function getRuleAuthorities(
  ruleId: string,
  idToken: string,
): Promise<RuleAuthorityListOut> {
  return unwrap(
    await apiClient.GET('/api/rules/{rule_id}/authorities', {
      params: { path: { rule_id: ruleId } },
      headers: bearer(idToken),
    }),
  );
}
